import React, { useState, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, Link } from 'react-router-dom';
import { Container, Row, Col, Card, Form, Button, Table, Badge, Tabs, Tab, Nav } from 'react-bootstrap';
import { fetchLojaFisicaPicosQueda, fetchTopVendas } from '../../services/api';
import SearchBar from '../../components/SearchBar/SearchBar';
import Pagination from '../../components/Pagination/Pagination';
import Loading from '../../components/Loading/Loading';
import EmptyState from '../../components/EmptyState/EmptyState';
import ImageTooltip from '../../components/ImageTooltip/ImageTooltip';
import iconGringao from '../../assets/iconegringao.png';

import FiltrosEcommerce from '../../components/FiltrosEcommerce/FiltrosEcommerce';

const TopVendas = () => {
  const [loja, setLoja] = useState('ecommerce');
  const [activeTab, setActiveTab] = useState('top-vendas');

  // Estado para os filtros
  const [filtros, setFiltros] = useState({
    categoria: '',
    informacao: '',
    fornecedor: '', // string separada por vírgula
    ordenacao: '',
    dataInicio: '',
    dataFim: '',
    search: '' // busca textual
  });

  const { data: topVendasData, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['topVendas', filtros.dataInicio, filtros.dataFim],
    queryFn: () => fetchTopVendas({
      periodo: 3,
      limit: 100, // Aumentei o limite para permitir filtragem no front
      dataInicio: filtros.dataInicio || undefined,
      dataFim: filtros.dataFim || undefined
    }),
    staleTime: 1000 * 60 * 5, // 5 minutes
  });

  const topVendas = topVendasData?.data || { centro: [], caxias: [], ecommerce: [] };

  const handleLojaChange = (k) => {
    setLoja(k);
  };

  const handleFilterChange = (novosFiltros) => {
    setFiltros(prev => ({ ...prev, ...novosFiltros }));
  };

  const handleSearch = (term) => {
    setFiltros(prev => ({ ...prev, search: term }));
  };

  // Função para filtrar os dados no frontend (Categoria, Fornecedor, Busca, Informação)
  const filtrarDados = (listaProdutos) => {
    if (!listaProdutos) return [];

    return listaProdutos.filter(produto => {
      // Filtro de Busca (Código ou Nome)
      if (filtros.search) {
        const termo = filtros.search.toLowerCase();
        const matchNome = (produto.nome || produto.descricao || '').toLowerCase().includes(termo);
        const matchCodigo = (produto.codigo || produto.codigoProduto || '').toLowerCase().includes(termo);
        if (!matchNome && !matchCodigo) return false;
      }

      // Filtro de Fornecedor
      if (filtros.fornecedor && filtros.fornecedor.length > 0) {
        const fornecedoresArray = filtros.fornecedor.split(',').map(f => f.trim().toLowerCase());
        const prodFornecedor = (produto.fornecedor || '').toLowerCase();
        // Verifica se o fornecedor do produto está na lista selecionada (parcial match ou exato)
        const matchFornecedor = fornecedoresArray.some(f => prodFornecedor.includes(f));
        if (!matchFornecedor) return false;
      }

      // Filtro de Categoria (Assumindo que o produto tenha campo categoria, se não tiver, ignora ou tenta inferir)
      // Nota: O endpoint atual de top vendas nem sempre retorna categoria explicitamente no nível raiz do objeto
      // Se não tiver categoria, esse filtro pode esconder tudo, então cuidado.
      if (filtros.categoria && produto.categoria) {
        if (!produto.categoria.toLowerCase().includes(filtros.categoria.toLowerCase())) return false;
      }

      return true;
    });
  };

  const listaFiltrada = filtrarDados(topVendas[loja]);

  // Função para exportar CSV (agora implementada)
  const handleExportCSV = () => {
    if (!listaFiltrada || listaFiltrada.length === 0) {
      alert('Não há dados para exportar.');
      return;
    }

    const headers = ['Rank', 'Código', 'Produto', 'Fornecedor', 'Quantidade Vendida', 'Valor Total'];
    const csvContent = [
      headers.join(';'),
      ...listaFiltrada.map((item, index) => [
        index + 1,
        item.codigoProduto || item.codigo,
        `"${(item.descricao || item.nome || '').replace(/"/g, '""')}"`,
        `"${(item.fornecedor || '').replace(/"/g, '""')}"`,
        item.quantidadeTotal || item.qtdVendida,
        (item.valorTotal || 0).toFixed(2).replace('.', ',')
      ].join(';'))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `top_vendas_${loja}_${new Date().toISOString().slice(0, 10)}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const renderProductList = (products, title, colorClass) => {
    return (
      <Card className="shadow-sm border-0">
        <Card.Body className="p-0">
          <Table hover responsive className="mb-0 align-middle table-borderless">
            <thead className="bg-light sticky-top text-muted text-uppercase small font-weight-bold">
              <tr>
                <th style={{ width: '60px' }} className="text-center">Rank</th>
                <th style={{ width: '80px' }}>Imagem</th>
                <th>Produto</th>
                <th>Fornecedor</th>
                <th className="text-end">Qtd. Vendida</th>
                <th className="text-end">Valor Total</th>
              </tr>
            </thead>
            <tbody>
              {products.map((item, index) => (
                <tr key={item.codigoProduto || item.codigo} className="border-bottom">
                  <td className="text-center align-middle">
                    <Badge bg={index < 3 ? 'warning' : 'light'} text={index < 3 ? 'dark' : 'secondary'} className="rounded-circle p-2" style={{ width: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      {index + 1}
                    </Badge>
                  </td>
                  <td className="align-middle">
                    <div style={{ width: '50px', height: '50px' }}>
                      <ImageTooltip
                        src={item.imagemBase64 || item.imagem}
                        alt={item.descricao || item.nome}
                        salesHistory={item.historicoCompleto}
                        productCode={item.codigoProduto || item.codigo}
                        productDescription={item.descricao || item.nome}
                      >
                        <img
                          src={item.imagemBase64 || item.imagem || iconGringao}
                          alt={item.descricao || item.nome}
                          className="img-fluid rounded shadow-sm"
                          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        />
                      </ImageTooltip>
                    </div>
                  </td>
                  <td className="align-middle">
                    <div>
                      <div className="fw-bold text-dark">{item.codigoProduto || item.codigo}</div>
                      <div className="text-muted small text-truncate" style={{ maxWidth: '300px' }}>{item.descricao || item.nome}</div>
                    </div>
                  </td>
                  <td className="align-middle">
                    {item.fornecedor ? (
                      <Badge bg="light" text="dark" className="border fw-normal">
                        {item.fornecedor}
                      </Badge>
                    ) : <span className="text-muted small">-</span>}
                  </td>
                  <td className="text-end align-middle">
                    <span className="fw-bold text-dark">
                      {item.quantidadeTotal || item.qtdVendida} un
                    </span>
                  </td>
                  <td className="text-end fw-bold text-success align-middle">
                    {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(item.valorTotal)}
                  </td>
                </tr>
              ))}
              {products.length === 0 && (
                <tr>
                  <td colSpan="6" className="text-center py-5 text-muted">
                    <div className="d-flex flex-column align-items-center opacity-50">
                      <i className="bi bi-search fs-1 mb-2"></i>
                      <p className="mb-0">Nenhum produto encontrado com os filtros atuais.</p>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </Card.Body>
      </Card>
    );
  };

  return (
    <div>
      {/* Filtros Padronizados */}
      <div className="mb-3">
        <FiltrosEcommerce
          onFilterChange={handleFilterChange}
          onSearch={handleSearch}
          initialDataInicio={filtros.dataInicio}
          initialDataFim={filtros.dataFim}
          onExportCSV={handleExportCSV}
          showInformacao={false}
          showOrdenacao={false}
        />
      </div>

      <Tab.Container id="store-tabs" defaultActiveKey="ecommerce" onSelect={handleLojaChange}>
        <div className="d-flex justify-content-between align-items-center mb-3">
          <Nav variant="pills" className="bg-white p-1 rounded shadow-sm">
            <Nav.Item>
              <Nav.Link eventKey="ecommerce" className="px-3">E-commerce</Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link eventKey="centro" className="px-3">Loja Centro</Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link eventKey="caxias" className="px-3">Loja Caxias</Nav.Link>
            </Nav.Item>
          </Nav>
          <div className="text-muted small">
            Exibindo {listaFiltrada.length} produtos
          </div>
        </div>

        {isLoading && <Loading />}

        {isError && (
          <div className="alert alert-danger shadow-sm border-0">
            Erro ao carregar top vendas: {error?.message}
          </div>
        )}

        {!isLoading && !isError && (
          <Tab.Content>
            <Tab.Pane eventKey="ecommerce">
              {renderProductList(listaFiltrada, 'Top Vendas - E-commerce', 'info')}
            </Tab.Pane>
            <Tab.Pane eventKey="centro">
              {renderProductList(listaFiltrada, 'Top Vendas - Loja Centro', 'primary')}
            </Tab.Pane>
            <Tab.Pane eventKey="caxias">
              {renderProductList(listaFiltrada, 'Top Vendas - Loja Caxias', 'success')}
            </Tab.Pane>
          </Tab.Content>
        )}
      </Tab.Container>
    </div>
  );
};

const AnaliseQueda = () => {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [thresholdPercent, setThresholdPercent] = useState(30);
  const [minPeakQuantity, setMinPeakQuantity] = useState(30);
  const [sortConfig, setSortConfig] = useState({ field: 'mesPicoQuantidade', direction: 'desc' });
  const [filtroMesPico, setFiltroMesPico] = useState('');
  const [estoqueMin, setEstoqueMin] = useState('');
  const [estoqueMax, setEstoqueMax] = useState('');
  const [motivoEstoque, setMotivoEstoque] = useState(true);
  const [motivoPreco, setMotivoPreco] = useState(true);
  const [motivoIndefinido, setMotivoIndefinido] = useState(true);

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['picosQueda', { page, search, thresholdPercent, minPeakQuantity }],
    queryFn: async () => {
      return fetchLojaFisicaPicosQueda({
        page,
        limit: 50,
        thresholdPercent,
        minPeakQuantity,
        search
      });
    },
    keepPreviousData: true
  });

  const handleThresholdChange = (e) => {
    setPage(1);
    setThresholdPercent(Number(e.target.value) || 0);
  };

  const handleMinPeakChange = (e) => {
    setPage(1);
    setMinPeakQuantity(Number(e.target.value) || 0);
  };

  const handleSearch = useCallback((value) => {
    setSearch(value);
    setPage(1);
  }, []);

  const handlePageChange = (newPage) => {
    setPage(newPage);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSort = useCallback((field) => {
    setSortConfig(prevConfig => {
      if (prevConfig.field === field) {
        return {
          field,
          direction: prevConfig.direction === 'asc' ? 'desc' : 'asc'
        };
      }
      return {
        field,
        direction: field === 'totalVendas' ? 'desc' : 'asc'
      };
    });
  }, []);

  const items = data?.data || [];

  // Aplicar filtros avançados em memória
  const filtradosAvancados = items.filter((item) => {
    // Filtro mês de pico
    if (filtroMesPico) {
      const mesNome = item.mesPico?.mesNome || '';
      if (mesNome !== filtroMesPico) {
        return false;
      }
    }

    // Filtro estoque min/máx
    const estoqueAtual = parseFloat(item.estoqueAtual) || 0;
    if (estoqueMin !== '' && estoqueAtual < Number(estoqueMin)) {
      return false;
    }
    if (estoqueMax !== '' && estoqueAtual > Number(estoqueMax)) {
      return false;
    }

    // Filtro motivo provável
    const motivo = item.motivoPrincipal || 'indefinido';
    if (motivo === 'possivel_falta_estoque' && !motivoEstoque) {
      return false;
    }
    if (motivo === 'possivel_variacao_preco' && !motivoPreco) {
      return false;
    }
    if (motivo === 'indefinido' && !motivoIndefinido) {
      return false;
    }

    return true;
  });

  // Ordenação local similar ao E-commerce, mas usando campos da análise de picos
  const sortedItems = [...filtradosAvancados].sort((a, b) => {
    const { field, direction } = sortConfig;
    let aValue;
    let bValue;

    switch (field) {
      case 'codigoInterno':
        aValue = (a.codigoInterno || '').toString().toLowerCase();
        bValue = (b.codigoInterno || '').toString().toLowerCase();
        return direction === 'asc' ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
      case 'descricao':
        aValue = (a.descricao || '').toString().toLowerCase();
        bValue = (b.descricao || '').toString().toLowerCase();
        return direction === 'asc' ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
      case 'estoqueAtual':
        aValue = parseFloat(a.estoqueAtual) || 0;
        bValue = parseFloat(b.estoqueAtual) || 0;
        break;
      case 'mesPicoQuantidade':
        aValue = a.mesPico?.quantidade || 0;
        bValue = b.mesPico?.quantidade || 0;
        break;
      default:
        aValue = 0;
        bValue = 0;
    }

    return direction === 'asc' ? aValue - bValue : bValue - aValue;
  });

  const pagination = data?.pagination || { page: 1, totalPages: 1, total: 0 };

  const getSortIcon = (field) => {
    if (sortConfig.field !== field) return <span className="text-muted ms-1" style={{ fontSize: '0.8em' }}>⇅</span>;
    return sortConfig.direction === 'asc' ? <span className="text-primary ms-1">↑</span> : <span className="text-primary ms-1">↓</span>;
  };

  return (
    <>
      <Card className="shadow mb-4">
        <Card.Header className="py-3 bg-white">
          <h6 className="m-0 font-weight-bold text-primary">Filtros de Análise</h6>
        </Card.Header>
        <Card.Body>
          <Row className="mb-3">
            <Col md={12}>
              <SearchBar onSearch={handleSearch} placeholder="Buscar por código ou descrição" />
            </Col>
          </Row>

          <Row>
            <Col md={3} className="mb-3">
              <Form.Group>
                <Form.Label>Queda máxima pós-pico (%)</Form.Label>
                <Form.Control
                  type="number"
                  min={1}
                  max={100}
                  value={thresholdPercent}
                  onChange={handleThresholdChange}
                />
              </Form.Group>
            </Col>
            <Col md={3} className="mb-3">
              <Form.Group>
                <Form.Label>Mínimo de unidades no pico</Form.Label>
                <Form.Control
                  type="number"
                  min={1}
                  value={minPeakQuantity}
                  onChange={handleMinPeakChange}
                />
              </Form.Group>
            </Col>
            <Col md={3} className="mb-3">
              <Form.Group>
                <Form.Label>Mês de pico</Form.Label>
                <Form.Select
                  value={filtroMesPico}
                  onChange={(e) => setFiltroMesPico(e.target.value)}
                >
                  <option value="">Todos</option>
                  {['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'].map(mes => (
                    <option key={mes} value={mes}>{mes}</option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={3} className="mb-3">
              <Form.Group>
                <Form.Label>Estoque (mín/máx)</Form.Label>
                <div className="d-flex gap-2">
                  <Form.Control
                    type="number"
                    placeholder="Mín"
                    value={estoqueMin}
                    onChange={(e) => setEstoqueMin(e.target.value)}
                  />
                  <Form.Control
                    type="number"
                    placeholder="Máx"
                    value={estoqueMax}
                    onChange={(e) => setEstoqueMax(e.target.value)}
                  />
                </div>
              </Form.Group>
            </Col>
          </Row>

          <Row>
            <Col md={12}>
              <Form.Label>Motivo provável</Form.Label>
              <div className="d-flex gap-3">
                <Form.Check
                  type="checkbox"
                  label="Estoque"
                  checked={motivoEstoque}
                  onChange={(e) => setMotivoEstoque(e.target.checked)}
                />
                <Form.Check
                  type="checkbox"
                  label="Preço"
                  checked={motivoPreco}
                  onChange={(e) => setMotivoPreco(e.target.checked)}
                />
                <Form.Check
                  type="checkbox"
                  label="Indefinido"
                  checked={motivoIndefinido}
                  onChange={(e) => setMotivoIndefinido(e.target.checked)}
                />
              </div>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {isLoading && <Loading />}

      {!isLoading && isError && (
        <div className="alert alert-danger">
          Erro ao carregar análise: {error?.message || 'Erro desconhecido'}
        </div>
      )}

      {!isLoading && !isError && sortedItems.length === 0 && (
        <EmptyState message="Nenhum produto encontrado com os critérios atuais." />
      )}

      {!isLoading && !isError && sortedItems.length > 0 && (
        <Card className="shadow mb-4">
          <Card.Header className="py-3 bg-white">
            <h6 className="m-0 font-weight-bold text-primary">Resultados da Análise</h6>
          </Card.Header>
          <Card.Body className="p-0">
            <div className="table-responsive">
              <Table hover responsive className="mb-0 align-middle">
                <thead className="bg-light">
                  <tr>
                    <th style={{ cursor: 'pointer' }} onClick={() => handleSort('codigoInterno')}>
                      Código {getSortIcon('codigoInterno')}
                    </th>
                    <th style={{ cursor: 'pointer' }} onClick={() => handleSort('descricao')}>
                      Descrição {getSortIcon('descricao')}
                    </th>
                    <th style={{ cursor: 'pointer' }} onClick={() => handleSort('mesPicoQuantidade')}>
                      Mês de Pico {getSortIcon('mesPicoQuantidade')}
                    </th>
                    <th>Meses após o Pico</th>
                    <th style={{ cursor: 'pointer' }} onClick={() => handleSort('estoqueAtual')}>
                      Estoque {getSortIcon('estoqueAtual')}
                    </th>
                    <th>Motivo Provável</th>
                    <th>Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedItems.map((item) => (
                    <tr key={item.codigoInterno}>
                      <td>
                        <div className="d-flex align-items-center">
                          <div style={{ width: '40px', height: '40px', marginRight: '10px' }}>
                            <ImageTooltip
                              src={item.imagemBase64}
                              alt={item.descricao}
                              salesHistory={item.historicoCompleto}
                              productCode={item.codigoInterno}
                              productDescription={item.descricao}
                            >
                              <img
                                src={item.imagemBase64 || iconGringao}
                                alt={item.descricao}
                                className="img-fluid rounded"
                                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                              />
                            </ImageTooltip>
                          </div>
                          <span className="fw-bold">{item.codigoInterno}</span>
                        </div>
                      </td>
                      <td>
                        <div className="text-truncate" style={{ maxWidth: '300px' }}>
                          {item.descricao}
                        </div>
                      </td>
                      <td>
                        <div className="fw-bold">{item.mesPico?.mesNome}/{item.mesPico?.ano}</div>
                        <small className="text-muted">
                          Pico: {item.mesPico?.quantidade} un
                        </small>
                      </td>
                      <td>
                        {item.mesesPosteriores.map((m) => (
                          <div key={`${m.ano} -${m.mesNumero} `} className="small text-muted">
                            {m.mesNome}/{m.ano}: {m.quantidade} un ({m.percentualEmRelacaoAoPico}% do pico)
                          </div>
                        ))}
                      </td>
                      <td>
                        <div className="fw-bold">{item.estoqueAtual}</div>
                        {item.riscoRuptura && (
                          <Badge bg="warning" text="dark" className="mt-1">
                            {item.riscoRuptura}
                          </Badge>
                        )}
                      </td>
                      <td>
                        <div className="d-flex flex-column gap-1">
                          <Badge
                            bg={
                              item.motivoPrincipal === 'possivel_falta_estoque' ? 'danger' :
                                item.motivoPrincipal === 'possivel_variacao_preco' ? 'warning' :
                                  item.motivoPrincipal === 'multiplos_fatores' ? 'info' : 'secondary'
                            }
                          >
                            {item.motivoPrincipal === 'possivel_falta_estoque' && 'Falta de Estoque'}
                            {item.motivoPrincipal === 'possivel_variacao_preco' && 'Variação de Preço'}
                            {item.motivoPrincipal === 'multiplos_fatores' && 'Múltiplos Fatores'}
                            {item.motivoPrincipal === 'indefinido' && 'Indefinido'}
                          </Badge>
                          {item.motivos && item.motivos.length > 0 && (
                            <ul className="mb-0 ps-3 small text-muted">
                              {item.motivos.map((motivo, index) => (
                                <li key={index}>{motivo}</li>
                              ))}
                            </ul>
                          )}
                        </div>
                      </td>
                      <td>
                        <Link to={`/ loja - fisica / ${item.codigoInterno} `} className="btn btn-sm btn-primary">
                          Ver detalhes
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          </Card.Body>
          <Card.Footer className="py-3 bg-white">
            <Pagination
              currentPage={page}
              totalPages={pagination.totalPages || 1}
              onPageChange={handlePageChange}
            />
          </Card.Footer>
        </Card>
      )}
    </>
  );
};

const PicosQueda = () => {
  const navigate = useNavigate();
  const [key, setKey] = useState('top-vendas');

  return (
    <Container fluid className="p-4">
      <div className="d-flex align-items-center mb-4">
        <Button
          variant="link"
          className="p-0 me-3 text-decoration-none text-secondary"
          onClick={() => navigate(-1)}
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M15 19l-7-7 7-7" />
          </svg>
        </Button>
        <div>
          <h1 className="h3 mb-0 text-gray-800">Análise de Vendas</h1>
          <p className="mb-0 text-muted">Picos de vendas, produtos top sellers e análise de quedas</p>
        </div>
      </div>

      <Tabs
        id="picos-vendas-tabs"
        activeKey={key}
        onSelect={(k) => setKey(k)}
        className="mb-4"
      >
        <Tab eventKey="top-vendas" title="🏆 Top Vendas (Picos)">
          <TopVendas />
        </Tab>
        <Tab eventKey="picos-queda" title="📉 Picos com Queda">
          <AnaliseQueda />
        </Tab>
      </Tabs>
    </Container>
  );
};

export default PicosQueda;
